/**
 * WebSocket logout relay for cross-app Single Logout (SLO).
 *
 * Connects to the gateway's WS endpoint when SLO is enabled.
 * On receiving a logout message, redirects through the gateway + Auth0
 * /v2/logout to kill all sessions.
 *
 * Also exposes broadcastLogout() for the logout flow to notify
 * other apps before navigating away.
 */

import { resolveEnv } from '@/config/environments';

const RECONNECT_DELAY_MS = 3_000;

const { loginApiUrl, appTarget } = resolveEnv();

let ws: WebSocket | null = null;
let registeredEmail: string | null = null;
let relayUrl: string | null = null;
let shouldConnect = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

function connect(): void {
  if (!relayUrl || !registeredEmail || !shouldConnect) return;

  // Guard against duplicate connections (including CONNECTING state)
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  try {
    const socket = new WebSocket(relayUrl);
    // Assign immediately so stopLogoutRelay can close in-flight connections
    ws = socket;

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'register', userId: registeredEmail }));
    };

    socket.onmessage = (event: MessageEvent) => {
      try {
        // Type assertion justified: MessageEvent.data is typed as any in browser API
        const msg = JSON.parse(event.data as string);
        if (msg.type === 'logout') {
          shouldConnect = false;
          socket.close();
          const returnTo = encodeURIComponent(globalThis.location.origin);
          globalThis.location.href = `${loginApiUrl}/auth/logout/${appTarget}?returnTo=${returnTo}&federated=true`;
        }
      } catch {
        // Ignore malformed messages
      }
    };

    socket.onclose = () => {
      // Only clear ref if this is still the active socket
      if (ws === socket) {
        ws = null;
      }
      if (shouldConnect) {
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };

    socket.onerror = () => {
      socket.close();
    };
  } catch {
    ws = null;
    if (shouldConnect) {
      reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
    }
  }
}

/**
 * Start the WS connection for a given user. Call after login is confirmed.
 *
 * @param email - The logged-in user's email, used as the relay routing key.
 * @param wsUrl - The WebSocket relay URL, provided by the BFF state response.
 *                Empty/missing disables the relay.
 */
export function startLogoutRelay(email: string, wsUrl: string): void {
  if (!wsUrl) return;
  registeredEmail = email;
  relayUrl = wsUrl;
  shouldConnect = true;
  connect();
}

/** Stop the WS connection. Call on unmount. */
export function stopLogoutRelay(): void {
  shouldConnect = false;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
  registeredEmail = null;
  relayUrl = null;
}

/** Broadcast logout to other apps via WS relay. Call before navigating to logout URL. */
export function broadcastLogout(): void {
  if (ws?.readyState === WebSocket.OPEN && registeredEmail) {
    ws.send(JSON.stringify({ type: 'logout', userId: registeredEmail }));
  } else {
    console.warn('[SLO] Cannot broadcast - ws state:', ws?.readyState ?? 'null');
  }
}

/** Returns true if the WS relay is connected (SLO is active). */
export function isRelayConnected(): boolean {
  return ws?.readyState === WebSocket.OPEN;
}
